import React, {useEffect, useRef, useState} from "react";
import {connect} from "react-redux";
import TextField from "../../components/elements/TextField";
import "pages/Auth/VerificationCodeViewV2.sass";
import cleanPhoneNumber from "../../methods/cleanPhoneNumber";
import {toHumanTime, formatDialPhoneForDisplay, dialPhoneTelUri} from "../../funcs";
import {DidntReceiveCode, notifyUserAboutCode} from "./DidntReceiveCode";
import {CircularProgress} from "@mui/material";
import {fetchReverseCallStatus, fetchVerificationDisabledCodeChannels, sendVerificationCode} from "../../methods/sendVerificationCode";
import {checkApiReachable} from "../../methods/checkApiReachable";
import {notifyUser} from "../../methods/notifyUser";
import { logSigningEvent, SigningEventType } from "../../methods/logSigningEvent";
import { brandingCssClass } from "../../utils/contractPageBranding";
import {CodeRequestStatus, renderVerificationCodeHint} from "./verificationCodeHints";
import {reverseCallNumberWaitTimedOut} from "./verificationCodeTimeouts";

function verificationFailureText(error: unknown): string | null {
    const data = (error as {response?: {data?: unknown}})?.response?.data
    if (typeof data !== "string") return null
    const text = data.trim()
    if (!text || text.startsWith("Произошла внутренняя ошибка")) return null
    return text
}

function scrollNodeIntoView(el: HTMLElement, block: ScrollLogicalPosition) {
    el.scrollIntoView({block, behavior: "smooth", inline: "nearest"})
}

function isPrimaryInVisualViewport(el: HTMLElement) {
    const rect = el.getBoundingClientRect()
    const vv = window.visualViewport
    const viewTop = vv ? vv.offsetTop : 0
    const viewBottom = viewTop + (vv ? vv.height : window.innerHeight)
    const margin = 12
    return rect.top >= viewTop + margin && rect.bottom <= viewBottom - margin
}

type Props = {
    verification_code: string
    code_channel: string,
    phone_number: string,
    disabled_code_channels: string[],
    updateVerificationCode: Function,
    updateCodeChannel: Function,
    finish: Function,
    back: Function,
    contractId?: string,
    purpose: string,
    purpose_meta?: Record<string, any>,
    branded?: boolean,
    $store: any
}

const RESEND_SMS_COUNTDOWN = 10
const API_REACHABILITY_POLL_MS = 15000

let VerificationCodeView: any = function EntityFormPopup(props: Props) {
    const {verification_code, code_channel, phone_number, disabled_code_channels, updateVerificationCode, updateCodeChannel, finish, back, contractId, purpose, purpose_meta, $store} = props

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isResending, setIsResending] = useState(false)
    const [codeRequestStatus, setCodeRequestStatus] = useState<CodeRequestStatus>("sent")
    const [codeRequestError, setCodeRequestError] = useState<string | null>(null)
    const [apiReachable, setApiReachable] = useState<boolean | null>(null)

    const [resendSmsCountdown, setResendSmsCountdown] = useState(RESEND_SMS_COUNTDOWN)
    const [reverseCallDialPhone, setReverseCallDialPhone] = useState<string | null>(null)
    const [reverseCallWaiting, setReverseCallWaiting] = useState(false)
    const [displayChannel, setDisplayChannel] = useState(code_channel)
    const [mergedDisabledCodeChannels, setMergedDisabledCodeChannels] = useState<string[]>(disabled_code_channels || [])
    const [reverseFlashCallEnabled, setReverseFlashCallEnabled] = useState<boolean | null>(null)
    const rootRef = useRef<HTMLDivElement>(null)
    const primaryRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const skipChannelScrollRef = useRef(true)
    let timer = setTimeout(() => {}, 0)

    const isRequestingCode = codeRequestStatus === "sending" || isResending

    useEffect(() => {
        setDisplayChannel(code_channel)
    }, [code_channel])

    useEffect(() => {
        let cancelled = false

        async function syncApiReachable() {
            const reachable = await checkApiReachable()
            if (!cancelled) {
                setApiReachable(reachable)
            }
        }

        syncApiReachable()
        const pollId = window.setInterval(syncApiReachable, API_REACHABILITY_POLL_MS)
        const onFocus = () => {
            syncApiReachable()
        }
        window.addEventListener("focus", onFocus)

        return () => {
            cancelled = true
            window.clearInterval(pollId)
            window.removeEventListener("focus", onFocus)
        }
    }, [])

    useEffect(() => {
        if (resendSmsCountdown === 0) return
        timer = setTimeout(() => setResendSmsCountdown(resendSmsCountdown - 1), 1000)
        return () => {
            clearTimeout(timer)
        }
    }, [resendSmsCountdown])

    useEffect(() => {
        setMergedDisabledCodeChannels(disabled_code_channels || [])
    }, [disabled_code_channels])

    useEffect(() => {
        let cancelled = false
        fetchVerificationDisabledCodeChannels(contractId).then(({disabledCodeChannels, reverseFlashCallEnabled: enabled}) => {
            if (cancelled) return
            const merged = Array.from(new Set([...(disabled_code_channels || []), ...disabledCodeChannels]))
            setMergedDisabledCodeChannels(merged)
            setReverseFlashCallEnabled(enabled)
        }).catch(() => {})
        return () => {
            cancelled = true
        }
    }, [contractId, disabled_code_channels])

    useEffect(() => {
        if (displayChannel !== "reverse-call") {
            setReverseCallWaiting(false)
            return
        }

        let cancelled = false

        async function syncReverseCallStatus() {
            const startedAt = Date.now()
            try {
                setReverseCallWaiting(true)
                while (!cancelled) {
                    const nextStatus = await fetchReverseCallStatus(cleanPhoneNumber(phone_number))
                    if (cancelled) return
                    if (nextStatus.dial_phone) {
                        setReverseCallDialPhone(nextStatus.dial_phone)
                    }
                    if (nextStatus.confirmed && nextStatus.code) {
                        updateVerificationCode(nextStatus.code)
                        setReverseCallWaiting(false)
                        notifyUser(props, 'Звонок получен', 'Код подставлен автоматически — нажмите «Завершить».')
                        return
                    }
                    if (!nextStatus.dial_phone && reverseCallNumberWaitTimedOut(startedAt, Date.now())) {
                        setReverseCallWaiting(false)
                        return
                    }
                    await new Promise(resolve => setTimeout(resolve, 2000))
                }
            } catch {
                if (!cancelled) {
                    setReverseCallWaiting(false)
                }
            }
        }

        syncReverseCallStatus()
        return () => {
            cancelled = true
        }
    }, [displayChannel, phone_number])

    useEffect(() => {
        const root = rootRef.current
        const vv = window.visualViewport
        if (!root) return

        let lastInset = 0
        const syncKeyboardInset = () => {
            const inset = vv
                ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
                : 0
            root.style.setProperty("--keyboard-inset", `${inset}px`)

            const keyboardJustOpened = inset > lastInset + 80
            lastInset = inset
            const primary = primaryRef.current
            if (keyboardJustOpened && primary && !isPrimaryInVisualViewport(primary)) {
                scrollNodeIntoView(primary, "nearest")
            }
        }

        syncKeyboardInset()
        if (!vv) return
        vv.addEventListener("resize", syncKeyboardInset)
        return () => {
            vv.removeEventListener("resize", syncKeyboardInset)
        }
    }, [])

    useEffect(() => {
        const input = inputRef.current
        if (!input) return
        const focusTimer = window.setTimeout(() => {
            input.focus({preventScroll: true})
        }, 50)
        return () => window.clearTimeout(focusTimer)
    }, [])

    useEffect(() => {
        if (skipChannelScrollRef.current) {
            skipChannelScrollRef.current = false
            return
        }
        const root = rootRef.current
        if (!root) return
        scrollNodeIntoView(root, "start")
    }, [displayChannel])

    async function requestVerificationCode(channel: string) {
        if (isResending) return

        setIsResending(true)
        setCodeRequestStatus("sending")
        try {
            const result = await sendVerificationCode(props, cleanPhoneNumber(phone_number), channel, purpose, {
                ...purpose_meta,
                channel: channel,
                contract_id: contractId
            })
            const usedChannel = result.usedChannel || channel
            if (usedChannel === "reverse-call") {
                if (result.dialPhone) {
                    setReverseCallDialPhone(result.dialPhone)
                }
                if (result.reverseCallCode) {
                    updateVerificationCode(result.reverseCallCode)
                    setReverseCallWaiting(false)
                } else {
                    setReverseCallWaiting(true)
                }
            } else {
                setReverseCallDialPhone(null)
                setReverseCallWaiting(false)
            }
            setDisplayChannel(usedChannel)
            updateCodeChannel(usedChannel)
            if (result.disabledCodeChannels?.length) {
                setMergedDisabledCodeChannels(result.disabledCodeChannels)
            }
            if (typeof result.reverseFlashCallEnabled === 'boolean') {
                setReverseFlashCallEnabled(result.reverseFlashCallEnabled)
            }
            setCodeRequestStatus("sent")
            setCodeRequestError(null)
            setApiReachable(true)
            notifyUserAboutCode(props, usedChannel)
            clearTimeout(timer)
            setResendSmsCountdown(RESEND_SMS_COUNTDOWN)
        } catch (error) {
            setCodeRequestStatus("failed")
            setCodeRequestError(verificationFailureText(error))
            if (!(error as any)?.response) setApiReachable(false)
        } finally {
            setIsResending(false)
        }
    }

    function renderDialPhoneLink() {
        if (!reverseCallDialPhone) {
            if (reverseCallWaiting || codeRequestStatus === "sending") {
                return (
                    <span style={{display: "inline-flex", alignItems: "center", gap: 6, verticalAlign: "middle"}}>
                        <CircularProgress size={14} sx={{color: "#0070FF"}} />
                        <span>получаем номер…</span>
                    </span>
                )
            }
            return <span>не удалось получить номер — попробуйте «Выслать код ещё раз»</span>
        }

        const telUri = dialPhoneTelUri(reverseCallDialPhone)
        const formatted = formatDialPhoneForDisplay(reverseCallDialPhone)
        if (!telUri) {
            return <b>{formatted}</b>
        }

        return (
            <a href={telUri} className="verification-code-v2__hint-link" style={{fontWeight: 700}}>
                {formatted}
            </a>
        )
    }

    function verificationCodePlaceholder() {
        if (codeRequestStatus === "sending") return "Отправляем код…"
        if (displayChannel == "reverse-call") return reverseCallWaiting ? "Ожидаем ваш звонок..." : "Код подставится после звонка"
        if (displayChannel == "tg-gateway") return "Код из Telegram"
        if (displayChannel == "tg") return "Код из телеграм бота"
        if (displayChannel == "max") return "Код из MAX бота"
        if (displayChannel == "sms") return "Код из SMS"
        if (displayChannel == "whatsapp") return "Код из WhatsApp"
        if (displayChannel == "call") return "4 последние цифры номера"
    }

    function renderStatusBanner() {
        if (codeRequestStatus === "sending") {
            return (
                <div className="verification-code-v2__status-banner verification-code-v2__status-banner--info" role="status">
                    <CircularProgress size={14} sx={{color: "#0070FF"}} />
                    <span>Отправляем код, подождите…</span>
                </div>
            )
        }
        if (codeRequestStatus === "failed") {
            return (
                <div className="verification-code-v2__status-banner verification-code-v2__status-banner--error" role="alert">
                    {codeRequestError ? "Не удалось отправить код." : "Не удалось отправить код. Проверьте интернет и попробуйте ещё раз."}
                </div>
            )
        }
        if (apiReachable === false) {
            return (
                <div className="verification-code-v2__status-banner verification-code-v2__status-banner--warning" role="status">
                    Не удаётся связаться с сервером. Отключите VPN, если он включён, и попробуйте другую сеть — мобильный интернет (LTE) или другой Wi‑Fi. Затем нажмите «Выслать код ещё раз».
                </div>
            )
        }
        return null
    }

    const numericCode = displayChannel !== "reverse-call"

    return <div
        ref={rootRef}
        className={`verification-code-v2 ui-kit-v2 ${brandingCssClass(Boolean(props.branded))}`.trim()}
    >
        {renderStatusBanner()}

        {renderVerificationCodeHint(codeRequestStatus, {
            channel: displayChannel,
            phone_number,
            reverseCallWaiting,
            renderDialPhoneLink,
            failureText: codeRequestError,
        })}

        <form onSubmit={async (e) => {
            e.preventDefault()

            if (isSubmitting || isRequestingCode) return
            setIsSubmitting(true)

            if (displayChannel === "reverse-call" && !verification_code) {
                setIsSubmitting(false)
                notifyUser(props, 'Сначала позвоните', 'Позвоните на бесплатный номер — код подставится автоматически после звонка.')
                return
            }

            if (contractId) {
                logSigningEvent({
                    eventType: SigningEventType.CODE_VALIDATION,
                    contractId,
                    timeFromStart: 0,
                    timeFromLastEvent: 0,
                    role: undefined,
                    isAlreadyRegisteredUser: false
                });
            }

            try {
                setIsSubmitting(false)
                await finish(displayChannel)
            } catch (error) {
                setIsSubmitting(false)
                throw error
            }
        }}>
            <div className="verification-code-v2__primary" ref={primaryRef}>
                <div className="verification-code-v2__field verification-code-v2__input">
                    <TextField
                        autoFocus={false}
                        inputRef={inputRef}
                        placeholder={verificationCodePlaceholder()}
                        value={verification_code}
                        onChange={(e: any) => updateVerificationCode(e.target.value?.trim() ?? '')}
                        variant="outlined"
                        className="ui-kit-v2"
                        disabled={(displayChannel === "reverse-call" && reverseCallWaiting) || isRequestingCode}
                        inputMode={numericCode ? "numeric" : undefined}
                        autoComplete={numericCode ? "one-time-code" : undefined}
                        inputProps={numericCode ? {inputMode: "numeric", autoComplete: "one-time-code", pattern: "[0-9]*"} : undefined}
                    />
                </div>
                <div className="verification-code-v2__field">
                    <button
                        type="submit"
                        disabled={$store.loading || isSubmitting || isRequestingCode}
                        className="ui-kit-v2 verification-code-v2__btn-primary"
                    >
                        <span className="button-text">{isSubmitting ? "Подождите..." : "Завершить"}</span>
                    </button>
                </div>
            </div>

            <div className="verification-code-v2__field">
                <button
                    type="button"
                    disabled={$store.loading || isSubmitting || isRequestingCode}
                    className="ui-kit-v2 verification-code-v2__btn-secondary"
                    onClick={() => back()}
                >
                    <span className="button-text">Вернуться назад</span>
                </button>
            </div>
        </form>

        <div className="verification-code-v2__field" style={{textAlign: "left"}}>
            <button
                type="button"
                className="ui-kit-v2 verification-code-v2__resend"
                disabled={!!resendSmsCountdown || isRequestingCode}
                onClick={async () => await requestVerificationCode(displayChannel)}
            >
                {isRequestingCode ? (
                    <div style={{display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                        <CircularProgress size={16} sx={{color: "#0070FF"}} />
                        <span>Отправка...</span>
                    </div>
                ) : (
                    `Выслать код ещё раз${resendSmsCountdown ? ` (${toHumanTime(resendSmsCountdown)})` : ''}`
                )}
            </button>
        </div>

        <div className="verification-code-v2__alternatives">
            <DidntReceiveCode
                channel={displayChannel}
                disabled_code_channels={mergedDisabledCodeChannels}
                reverse_flash_call_enabled={reverseFlashCallEnabled}
                phone_number={phone_number}
                purpose={purpose}
                resendSmsCountdown={resendSmsCountdown}
                isRequestingCode={isRequestingCode}
                getCode={async (channel: string) => {
                    await requestVerificationCode(channel)
                }}
            />
        </div>
        {
            $store.loading &&
            <div style={{marginTop: "15px", display: "flex", justifyContent: "center"}}>
                <CircularProgress sx={{color: "#0070FF"}} />
            </div>
        }
    </div>
}

VerificationCodeView = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(VerificationCodeView)

export default VerificationCodeView
